import React, { type ReactElement } from 'react';
import { Theme, styled as materialStyled } from '@mui/material';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AlertModel from '../../components/AlertModal/AlertModal';
import { inviteActions } from '../../redux/invite/inviteSlice';
import Loader from '../../components/MyGemVault/Loader';
import { RootState } from '../../redux/store';
import { InviteStatus } from '../../utils/enums/invite-status.enum';

const CustomContainer = materialStyled(Container)(({ theme }: { theme: Theme }) => ({
  backgroundColor: theme.palette.white.main,
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  paddingBottom: '15px',
  alignItems: 'center',
  paddingTop: '24px',
}));

const CenteredBox = materialStyled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
});

function Invite(): ReactElement {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const componentStatus = useSelector((state: RootState) => state.invite.componentStatus);
  const inviteStatus = useSelector((state: RootState) => state.invite.inviteStatus);
  const isInformByUser = useSelector((state: RootState) => state.invite.isInformByUser);
  const queryParams = new URLSearchParams(location.search);
  const invalid = queryParams.get('invalid');
  const isFromNCC = location.pathname.startsWith('/ncc/invite/');

  React.useEffect(() => {
    if (token) {
      dispatch(inviteActions.getValidateInviteAsync(token));
    }
  }, [dispatch, token, componentStatus.recallInviteLoading]);

  const onRecall = () => {
    if (token) {
      dispatch(inviteActions.getRecallInviteAsync(token));
    }
  };

  const handleCancelInvalidInvite = () => {
    window.open('about:blank', '_self');
    window.close();
  };

  const getComponents = () => {
    if (componentStatus.validateTokenLoading || componentStatus.recallInviteLoading) {
      return <Loader text="Validating the link..." />;
    }

    if (inviteStatus === InviteStatus.EXPIRED) {
      return <AlertModel message="This link is expired!" type="warning" />;
    }
    if (
      componentStatus.error ||
      componentStatus.errorStatus === 404 ||
      ((inviteStatus !== InviteStatus.RECALLED || isInformByUser) && !invalid)
    ) {
      return <AlertModel message="This link is invalid" type="warning" />;
    }

    if (invalid) {
      if (inviteStatus === InviteStatus.RECALLED && isInformByUser) {
        return (
          <AlertModel
            message={
              <>
                <span>Thank you! </span>
                <span>The sender has been notified </span>
              </>
            }
            type="success"
          />
        );
      }
      if (inviteStatus === InviteStatus.RECALLED && !isInformByUser) {
        return (
          <AlertModel
            message={
              <>
                <span>The link is no longer valid as </span>
                <span>the Administrator has </span>
                <span>recalled the invitation</span>
              </>
            }
            type="warning"
          />
        );
      }

      return (
        <AlertModel
          message={
            <>
              <span>Are you sure you want to</span>
              <br />
              <span>inform the admin?</span>
            </>
          }
          type="warning"
          okText="Yes, inform"
          cancelText="Cancel"
          onClickOk={onRecall}
          onClickCancel={handleCancelInvalidInvite}
        />
      );
    }

    if (inviteStatus === InviteStatus.RECALLED && !isInformByUser) {
      return (
        <AlertModel
          message={
            <>
              <span>The link is no longer valid as </span>
              <span>the Administrator has </span>
              <span>recalled the invitation</span>
            </>
          }
          type="warning"
        />
      );
    }
  };
  React.useEffect(() => {
    if (inviteStatus === InviteStatus.PENDING && invalid !== 'true') {
      if (isFromNCC) {
        navigate(`/subordinates/ncc/sign-in?token=${token}`);
      } else {
        navigate(`/subordinates/sign-in?token=${token}`);
      }
    }
  }, [componentStatus, invalid]);

  return (
    <CustomContainer maxWidth={false}>
      <CenteredBox>{getComponents()}</CenteredBox>
    </CustomContainer>
  );
}

export default Invite;
